import { KpiOptionObject } from 'types/GlobalKpiOption'
import { TKeyMetric } from 'types/GlobalKeyMetric'
import { TMetricSettings } from 'types/GlobalMetric'

import { TDataDialogSource } from './types'

/**
 * Builders translating each caller's domain object into the dialog's generic
 * source descriptor, so callers never touch source types and the dialog
 * core never sees domain types like KpiOptionObject.
 */

export function buildMetricSource(params: {
  id: string
  settings: TMetricSettings
  entityId: string | null
  title: string
  unit: string | null
  chatSourceId?: string
}): TDataDialogSource {
  const { id, settings, entityId, title, unit, chatSourceId } = params

  return {
    views: [
      {
        id,
        sourceType: 'metric',
        title,
        unit,
        entityId,
        chatSourceId,
        metric: { id, settings }
      }
    ]
  }
}

// The view's `id` is the key metric id (used for the details fetch), while its
// metric anchor points at the underlying customized metric so the chart and
// chat match the table. `entity_id` (the underlying metric's entity, scoping
// column editing / AI-column highlighting) comes straight off the key metric.
export function buildKeyMetricSource(keyMetric: TKeyMetric): TDataDialogSource {
  return {
    views: [
      {
        id: keyMetric.id,
        sourceType: 'key_metric',
        title: keyMetric.title,
        unit: null,
        entityId: keyMetric.entity_id,
        metric: {
          id: keyMetric.metric.id,
          settings: keyMetric.metric.settings
        }
      }
    ]
  }
}

// All of a widget's KPIs become selectable views (sorted by their widget
// index); `initialId` is the one to open on. Options that cannot show details
// are listed but disabled.
export function buildKpiOptionSource(
  options: KpiOptionObject[],
  initialId: string
): TDataDialogSource {
  return {
    views: [...options]
      .sort((a, b) => a.index - b.index)
      .map((option) => ({
        id: option.id,
        sourceType: 'kpi_option',
        title: option.title,
        unit: option.unit,
        entityId: option.entity_id,
        disabled: !option.can_show_details,
        chatSourceId: option.id,
        metric: option.can_show_details
          ? {
              id: option.kpi_template_id,
              settings: {
                variant_kpi_template_id: option.variant_kpi_template_id,
                rolling_sum: option.rolling_sum,
                distribution: option.distribution,
                slices: option.slices
              }
            }
          : null
      })),
    initialId
  }
}
